import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  CardMedia,
  Fade,
  Icon,
  Modal,
  Popper,
  Skeleton,
  TextField,
  Typography,
} from '@mui/material'
import React, { useState } from 'react'
import { useMutation, useQueryClient } from 'react-query'
import useOpenClose from 'services/hooks/useOpenClose'
import { editInterview } from 'services/api/modules/recruitment/scheduler'
import AdxInfoModal from 'components/reusable/modal/AdxInfoModal'
import { useNavigate } from 'react-router-dom'
import { format } from 'date-fns'
import AdxButton from 'components/custom/AdxButton'

const CalendarDetailsModal = ({ data, dateData, open, handleClose, interviewSlotIsLoading }) => {
  const style = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    maxWidth: '600px',
    width: '100%',
    bgcolor: 'background.paper',
    boxShadow: 24,
    borderRadius: '4.5px',
    maxHeight: '80vh',
    outline: 'none',
    overflowY: 'hidden',
  }
  const navigate = useNavigate()

  // modal and popper states
  const noShowInterviewModalState = useOpenClose(false)
  const cancelInterviewModalState = useOpenClose(false)
  const completeInterviewModalState = useOpenClose(false)

  const [openPopper, setOpenPopper] = useState(false)
  const [anchorEl, setAnchorEl] = useState(null)
  const handleClick = (event) => {
    setAnchorEl(event.currentTarget)
    setOpenPopper((previousOpen) => !previousOpen)
  }
  const canBeOpen = openPopper && Boolean(anchorEl)

  // more details accordion
  const [expanded, setExpanded] = useState(false)
  const handleChange = (panel) => (event, isExpanded) => {
    setExpanded(isExpanded ? panel : false)
  }

  // // api call
  const queryClient = useQueryClient()
  const {
    mutate: editInterviewMutate,
    isLoading: editInterviewIsLoading,
    isError: editInterviewIsError,
  } = useMutation((payload) => editInterview(data?.id, payload), {
    onSuccess: async (response) => {
      await queryClient.invalidateQueries('listCalendar')
      await queryClient.invalidateQueries('getInterviewSlot')
      await queryClient.invalidateQueries('getAllApplicant')
      console.log(response)
      if (
        noShowInterviewModalState.isOpen ||
        cancelInterviewModalState.isOpen ||
        completeInterviewModalState.isOpen
      ) {
        noShowInterviewModalState.handleClose()
        cancelInterviewModalState.handleClose()
        completeInterviewModalState.handleClose()
        handleClose()
      }
    },
    onError: (error) => {
      console.log(error)
    },
  })

  const dateFormatter = (date) => {
    const currentDate = new Date(date)
    if (!date) {
      return null
    }
    const formattedDate = format(currentDate, 'EEEE, dd/MM/yyyy')
    return formattedDate
  }

  // Format date as desired (DD/MM/YYYY, TIME)
  const timestamp = new Date(data?.updated_at)
  const updatedTimeStamp = timestamp?.toLocaleString('en-GB', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
  })

  const amPmIndex = updatedTimeStamp?.lastIndexOf(' ') + 1
  const capitalizedAmPm = updatedTimeStamp?.slice(amPmIndex, amPmIndex + 2).toUpperCase()

  const formattedTimeStamp = updatedTimeStamp?.slice(0, amPmIndex) + capitalizedAmPm

  const currentTime = new Date()
  const dateTimeString = `${data?.end_date}T${data?.end_time}`
  const endDateTimeObject = new Date(dateTimeString)

  const checkIsPast = (date) => {
    return currentTime > date
  }
  const pendingStatus = checkIsPast(endDateTimeObject)

  const formattedStartTime = new Date(`2000-01-01T${data?.start_time}`).toLocaleTimeString(
    'en-US',
    {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
    }
  )

  const formattedEndTime = new Date(`2000-01-01T${data?.end_time}`).toLocaleTimeString('en-US', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
  })

  const candidateDetails = data?.relations?.jobAdCandidate?.relations?.candidateable

  return (
    <>
      <Modal
        open={open}
        onClose={() => {
          setExpanded(false)
          setOpenPopper(false)
          handleClose()
        }}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
      >
        <Box sx={style}>
          <Box
            display="flex"
            justifyContent="space-between"
            px="24px"
            py="15px"
            gap="12px"
            sx={{
              width: '100%',
              borderBottom: 1,
              borderColor: 'gray-line.main',
              position: 'fixed',
              zIndex: 1,
              backgroundColor: 'white.main',
              borderRadius: '4.5px 4.5px 0px 0px',
            }}
          >
            <Typography variant="subtitle1" color="dark-text.main" width="100%">
              Details
            </Typography>
            <Icon
              sx={{
                fontSize: '21px',
                cursor: 'pointer',
              }}
              onClick={() => {
                setOpenPopper(false)
                handleClose()
              }}
            >
              close
            </Icon>
          </Box>
          <Box
            sx={{
              overflowY: 'scroll',
              height: 'calc(80vh - 52px)',
              // height: '60vh',
              borderRadius: '0px 0px 4.5px 4.5px',
              marginTop: '52px',
            }}
            className="scrollbar"
          >
            <Box
              px="24px"
              py="32px"
              display="flex"
              flexDirection="column"
              justifyContent="center"
              alignItems="center"
              gap="24px"
              sx={{ width: '100%', borderBottom: 1, borderBottomColor: 'gray-line.main' }}
            >
              {pendingStatus && data?.status === 'scheduled' && (
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'start',
                    padding: '12px',
                    gap: '8px',
                    backgroundColor: 'light-error.main',
                    borderRadius: '4.5px',
                  }}
                >
                  <Icon sx={{ fontSize: '21px', color: 'warning.main' }}>error</Icon>
                  <Typography>
                    The interview session has passed. Please update the status of the interview.
                  </Typography>
                </Box>
              )}
              <Box
                display="flex"
                flexDirection="column"
                justifyContent="center"
                gap="12px"
                sx={{ width: '100%' }}
              >
                <Box>
                  {interviewSlotIsLoading ? (
                    <Skeleton variant="rounded" width="40%" />
                  ) : (
                    <>
                      <Typography fontWeight="600" variant="h4" color="dark-text.main">
                        {`${data?.title} (${candidateDetails?.name})`}
                      </Typography>
                    </>
                  )}
                </Box>
                <Box display="flex" gap="12px" alignItems="center">
                  {interviewSlotIsLoading ? (
                    <Skeleton variant="rounded" width="100%" />
                  ) : (
                    <>
                      <Icon sx={{ fontSize: '16px', color: 'light-gray-text.main' }}>
                        calendar_month
                      </Icon>
                      <Typography variant="body1" color="light-gray-text.main">
                        {dateFormatter(data?.start_date)}
                      </Typography>
                      <Typography variant="body1" color="light-gray-text.main">
                        •
                      </Typography>
                      <Typography variant="body1" color="light-gray-text.main">
                        {`${formattedStartTime} - ${formattedEndTime}`}
                      </Typography>
                    </>
                  )}
                </Box>
              </Box>
              {interviewSlotIsLoading ? (
                <Skeleton variant="rounded" width="100%" height={60} />
              ) : (
                <>
                  {data?.status === 'scheduled' && !pendingStatus ? (
                    <>
                      <Box
                        display="flex"
                        justifyContent="center"
                        alignItems="center"
                        gap="10px"
                        sx={{ width: '100%' }}
                      >
                        <AdxButton
                          variant={'outlined'}
                          onClick={() =>
                            navigate('/app/recruitment/schedule-interview/edit', {
                              state: {
                                is_edit: true,
                                ...data,
                              },
                            })
                          }
                          sx={{ width: '100%' }}
                        >
                          Edit or reschedule
                        </AdxButton>
                        <AdxButton
                          variant={'outlined'}
                          sx={{
                            width: '100%',
                            '&:hover': { backgroundColor: 'light-error.main' },
                          }}
                          color="error"
                          onClick={() => cancelInterviewModalState.handleOpen()}
                        >
                          Cancel interview
                        </AdxButton>
                      </Box>
                      {cancelInterviewModalState.isOpen && (
                        <CancelInterviewModal
                          open={cancelInterviewModalState.isOpen}
                          onClose={cancelInterviewModalState.handleClose}
                          isLoading={editInterviewIsLoading}
                          onClick={(remarks) =>
                            editInterviewMutate({
                              status: 2,
                              ...(remarks ? { remarks: remarks } : {}),
                            })
                          }
                          isError={editInterviewIsError}
                        />
                      )}
                    </>
                  ) : data?.status === 'scheduled' && pendingStatus ? (
                    <>
                      <Box
                        display="flex"
                        justifyContent="center"
                        alignItems="center"
                        gap="10px"
                        sx={{ width: '100%' }}
                      >
                        <AdxButton
                          variant={'contained'}
                          onClick={() => {
                            completeInterviewModalState.handleOpen()
                            setOpenPopper(false)
                          }}
                          sx={{ width: '100%', boxShadow: 'none' }}
                        >
                          Mark as completed
                        </AdxButton>
                        <AdxButton
                          variant={'outlined'}
                          sx={{
                            width: '100%',
                            '&:hover': { backgroundColor: 'alternate.light' },
                          }}
                          color="alternate"
                          onClick={handleClick}
                        >
                          More options
                          <Icon>{canBeOpen ? 'expand_less' : 'expand_more'} </Icon>
                          <Popper
                            style={{ zIndex: 9999999999999 }}
                            open={openPopper}
                            anchorEl={anchorEl}
                            placement="bottom-start"
                            transition
                          >
                            {({ TransitionProps }) => (
                              <Fade {...TransitionProps} timeout={50}>
                                <Box
                                  position="absolute"
                                  width="263px"
                                  sx={{
                                    backgroundColor: 'white.main',
                                    border: 1,
                                    borderColor: 'gray-line.main',
                                  }}
                                >
                                  <Box
                                    display="flex"
                                    alignItems="center"
                                    padding={2}
                                    borderBottom={1}
                                    borderColor="gray-line.main"
                                    columnGap={2}
                                    sx={{
                                      cursor: 'pointer',
                                      '&:hover': {
                                        backgroundColor: 'alternate.light',
                                      },
                                    }}
                                    onClick={() =>
                                      navigate('/app/recruitment/schedule-interview/edit', {
                                        state: {
                                          is_edit: true,
                                          ...data,
                                        },
                                      })
                                    }
                                  >
                                    <Icon sx={{ color: 'alternate.main' }}>calendar_month</Icon>
                                    <Typography>Edit or reschedule</Typography>
                                  </Box>
                                  <Box
                                    display="flex"
                                    alignItems="center"
                                    padding={2}
                                    borderBottom={1}
                                    borderColor="gray-line.main"
                                    columnGap={2}
                                    sx={{
                                      cursor: 'pointer',
                                      '&:hover': {
                                        backgroundColor: 'light-yellow-01.main',
                                      },
                                    }}
                                    onClick={() => noShowInterviewModalState.handleOpen()}
                                  >
                                    <Icon sx={{ color: 'warning.main' }}>help_outline</Icon>
                                    <Typography>Candidate no-show</Typography>
                                  </Box>
                                  <Box
                                    display="flex"
                                    alignItems="center"
                                    padding={2}
                                    columnGap={2}
                                    sx={{
                                      cursor: 'pointer',
                                      '&:hover': {
                                        backgroundColor: 'light-error.main',
                                      },
                                    }}
                                    onClick={() => cancelInterviewModalState.handleOpen()}
                                  >
                                    <Icon sx={{ color: 'error.main' }}>highlight_off</Icon>
                                    <Typography sx={{ color: 'error.main' }}>
                                      Cancel interview
                                    </Typography>
                                  </Box>
                                </Box>
                              </Fade>
                            )}
                          </Popper>
                        </AdxButton>
                      </Box>
                      {noShowInterviewModalState.isOpen && (
                        <NoShowInterviewModal
                          open={noShowInterviewModalState.isOpen}
                          onClose={noShowInterviewModalState.handleClose}
                          isLoading={editInterviewIsLoading}
                          onClick={(remarks) =>
                            editInterviewMutate({
                              status: 4,
                              ...(remarks ? { remarks: remarks } : {}),
                            })
                          }
                          isError={editInterviewIsError}
                        />
                      )}
                      {cancelInterviewModalState.isOpen && (
                        <CancelInterviewModal
                          open={cancelInterviewModalState.isOpen}
                          onClose={cancelInterviewModalState.handleClose}
                          isLoading={editInterviewIsLoading}
                          onClick={(remarks) =>
                            editInterviewMutate({
                              status: 2,
                              ...(remarks ? { remarks: remarks } : {}),
                            })
                          }
                          isError={editInterviewIsError}
                        />
                      )}
                      {completeInterviewModalState.isOpen && (
                        <CompleteInterviewModal
                          open={completeInterviewModalState.isOpen}
                          onClose={completeInterviewModalState.handleClose}
                          isLoading={editInterviewIsLoading}
                          onClick={() => editInterviewMutate({ status: 3 })}
                          isError={editInterviewIsError}
                        />
                      )}
                    </>
                  ) : data?.status === 'candidate-no-show' ? (
                    <>
                      <Box
                        sx={{
                          border: 1,
                          borderRadius: '4.5px',
                          borderColor: 'gray-line.main',
                          width: '100%',
                        }}
                      >
                        <Box
                          display="flex"
                          justifyContent="start"
                          alignItems="center"
                          gap="12px"
                          sx={{ width: '100%', borderBottom: 1, borderColor: 'gray-line.main' }}
                          p="16px"
                        >
                          <Icon sx={{ color: 'warning.main' }}>help</Icon>
                          <Typography>Candidate no-show</Typography>
                        </Box>
                        <Box
                          sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            justifyContent: 'center',
                            paddingX: '14px',
                            paddingY: '16px',
                            gap: '14px',
                            width: '100%',
                          }}
                        >
                          <Box>
                            <Typography>{data?.remarks}</Typography>
                          </Box>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <Typography variant="body1" color="light-gray-text.main">
                              {formattedTimeStamp}
                            </Typography>
                            <Typography variant="body1" color="light-gray-text.main">
                              •
                            </Typography>
                            <Typography variant="body1" color="light-gray-text.main">
                              {data?.remarks_by}
                            </Typography>
                          </Box>
                        </Box>
                      </Box>
                    </>
                  ) : data?.status === 'completed' ? (
                    <>
                      <Box
                        display="flex"
                        justifyContent="start"
                        alignItems="center"
                        gap="12px"
                        sx={{ width: '100%' }}
                        p="16px"
                        bgcolor="default-background.main"
                      >
                        <Icon sx={{ color: 'positive.main' }}>check_circle</Icon>
                        <Typography>Completed</Typography>
                      </Box>
                    </>
                  ) : (
                    <>
                      <Box
                        sx={{
                          border: 1,
                          borderRadius: '4.5px',
                          borderColor: 'gray-line.main',
                          width: '100%',
                        }}
                      >
                        <Box
                          display="flex"
                          justifyContent="start"
                          alignItems="center"
                          gap="12px"
                          sx={{ width: '100%', borderBottom: 1, borderColor: 'gray-line.main' }}
                          p="16px"
                        >
                          <Icon sx={{ color: 'error.main' }}>highlight_off</Icon>
                          <Typography>Cancelled</Typography>
                        </Box>
                        <Box
                          sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            justifyContent: 'center',
                            paddingX: '14px',
                            paddingY: '16px',
                            gap: '14px',
                            width: '100%',
                          }}
                        >
                          <Box>
                            <Typography>{data?.remarks}</Typography>
                          </Box>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <Typography variant="body1" color="light-gray-text.main">
                              {formattedTimeStamp}
                            </Typography>
                            <Typography variant="body1" color="light-gray-text.main">
                              •
                            </Typography>
                            <Typography variant="body1" color="light-gray-text.main">
                              {data?.remarks_by}
                            </Typography>
                          </Box>
                        </Box>
                      </Box>
                    </>
                  )}
                </>
              )}
            </Box>
            <Box
              display="flex"
              flexDirection="column"
              alignItems="start"
              padding="24px"
              gap="24px"
              sx={{ width: '100%' }}
            >
              <Box display="flex" flexDirection="column" gap="8px" sx={{ width: '100%' }}>
                <Box>
                  <Typography variant="body1" color="light-gray-text.main">
                    Candidate
                  </Typography>
                </Box>
                <Box
                  bgcolor="default-background.main"
                  display="flex"
                  alignItems="center"
                  paddingX="14px"
                  paddingY="16px"
                >
                  {interviewSlotIsLoading ? (
                    <Skeleton variant="circular" width={45} height={45} sx={{ marginRight: 2 }} />
                  ) : candidateDetails?.profile_image_url ? (
                    <Box>
                      <CardMedia
                        component="img"
                        sx={{
                          width: '40px',
                          height: '40px',
                          borderRadius: '100%',
                          objectFit: 'cover',
                          marginRight: 2,
                        }}
                        src={candidateDetails?.profile_image_url}
                      />
                    </Box>
                  ) : (
                    <Box
                      sx={{
                        width: '45px',
                        height: '45px',
                        backgroundColor: 'white.main',
                        borderRadius: '100%',
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        marginRight: 2,
                      }}
                    >
                      <Icon
                        sx={{
                          width: '20px',
                          height: '20px',
                          fontSize: '20px',
                          color: 'alternate.main',
                        }}
                      >
                        person_outline_outlined
                      </Icon>
                    </Box>
                  )}
                  <Box sx={{ width: '100%' }}>
                    {interviewSlotIsLoading ? (
                      <Skeleton variant="text" width="40%" />
                    ) : (
                      <>
                        <Typography fontWeight="600" variant="subtitle1" color="dark-text.main">
                          {candidateDetails?.name}
                        </Typography>
                      </>
                    )}
                    {interviewSlotIsLoading ? (
                      <Skeleton variant="text" width="60%" />
                    ) : (
                      <>
                        <Typography variant="body2" color="light-gray-text.main">
                          {candidateDetails?.email}
                        </Typography>
                      </>
                    )}
                  </Box>
                </Box>
              </Box>
              <Box display="flex" flexDirection="column" gap="8px" sx={{ width: '100%' }}>
                <Box>
                  <Typography variant="body1" color="light-gray-text.main">
                    {data?.relations?.attendee?.length === 1
                      ? `Interviewer (${data?.relations?.attendee?.length})`
                      : `Interviewers (${data?.relations?.attendee?.length})`}
                  </Typography>
                </Box>
                {data?.relations?.attendee?.map((item, index) => (
                  <Box key={index} display="flex" alignItems="center" paddingY="12px">
                    {interviewSlotIsLoading ? (
                      <Skeleton variant="circular" width={45} height={45} sx={{ marginRight: 2 }} />
                    ) : (
                      <>
                        {item?.type === 'External Guest' ? (
                          <Box
                            sx={{
                              width: '40px',
                              height: '40px',
                              backgroundColor: 'default-background.main',
                              borderRadius: '100%',
                              display: 'flex',
                              justifyContent: 'center',
                              alignItems: 'center',
                              marginRight: 2,
                            }}
                          >
                            <Icon
                              sx={{
                                width: '20px',
                                height: '20px',
                                fontSize: '20px',
                                color: 'alternate.main',
                              }}
                            >
                              mail_outlined
                            </Icon>
                          </Box>
                        ) : (
                          <CardMedia
                            component="img"
                            sx={{
                              marginRight: 2,
                              height: 45,
                              width: 45,
                              objectFit: 'cover',
                              borderRadius: '100%',
                            }}
                            src={item?.profile_photo_url}
                          />
                        )}
                      </>
                    )}
                    <Box sx={{ width: '100%' }}>
                      {interviewSlotIsLoading ? (
                        <Skeleton variant="text" width="40%" />
                      ) : (
                        <>
                          <Typography variant="body1" color="dark-text.main">
                            {item?.type === 'External Guest' ? 'External Guest' : item?.name}
                          </Typography>
                        </>
                      )}
                      {interviewSlotIsLoading ? (
                        <Skeleton variant="text" width="60%" />
                      ) : (
                        <>
                          <Typography variant="body2" color="light-gray-text.main">
                            {item?.email}
                          </Typography>
                        </>
                      )}
                    </Box>
                  </Box>
                ))}
              </Box>
              <Box display="flex" alignItems="center" gap="10px">
                {data?.description && (
                  <Accordion
                    disableGutters={true}
                    sx={{ boxShadow: 'none' }}
                    expanded={expanded === 'panel1'}
                    onChange={handleChange('panel1')}
                  >
                    {!expanded && (
                      <AccordionSummary aria-controls="panel1-content" id="panel1-header">
                        <Typography
                          variant="subtitle1"
                          color="alternate.main"
                          sx={{ textDecoration: 'underline' }}
                        >
                          More details
                        </Typography>
                        <Icon sx={{ color: 'alternate.main' }}>expand_more</Icon>
                      </AccordionSummary>
                    )}
                    <AccordionDetails>
                      <Box display="flex" flexDirection="column" gap="12px" sx={{ width: '100%' }}>
                        <Typography variant="body1" color="light-gray-text.main">
                          Interview mode
                        </Typography>
                        <Typography
                          variant="body2"
                          color="dark-text.main"
                          sx={{ textTransform: 'capitalize' }}
                        >
                          {data?.interview_mode}
                        </Typography>
                        {data?.location && (
                          <>
                            <Typography variant="body1" color="light-gray-text.main">
                              Location
                            </Typography>
                            <Typography variant="body2" color="dark-text.main">
                              {data?.location}
                            </Typography>
                          </>
                        )}
                        <Typography variant="body1" color="light-gray-text.main">
                          Description
                        </Typography>
                        <Typography variant="body2" color="dark-text.main">
                          {data?.description ?? 'No description'}
                        </Typography>
                        {expanded && (
                          <Box
                            display="flex"
                            alignItems="center"
                            gap="10px"
                            onClick={handleChange('panel1')}
                            sx={{ cursor: 'pointer' }}
                          >
                            <Typography
                              variant="subtitle1"
                              color="alternate.main"
                              sx={{ textDecoration: 'underline' }}
                            >
                              Less details
                            </Typography>
                            <Icon sx={{ color: 'alternate.main' }}>expand_less</Icon>
                          </Box>
                        )}
                      </Box>
                    </AccordionDetails>
                  </Accordion>
                )}
              </Box>
            </Box>
          </Box>
        </Box>
      </Modal>
    </>
  )
}

export default CalendarDetailsModal

const NoShowInterviewModal = ({ open, onClose, isLoading, onClick, isError }) => {
  const [textLength, setTextLength] = useState(0)
  const [remarks, setRemarks] = useState('')

  const style = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    maxWidth: '600px',
    width: '100%',
    bgcolor: 'background.paper',
    boxShadow: 24,
    borderRadius: '6.5px',
    padding: '40px 32px 32px 32px',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '40px',
    position: 'relative',
  }
  return (
    <Modal open={open} onClose={onClose}>
      <Box sx={style}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '6px',
          }}
        >
          <Typography variant="h4" sx={{ width: '341px', textAlign: 'center' }}>
            Are you sure with the status?
          </Typography>
          <Typography variant="body1">Please add the reason of the candidate's absence.</Typography>
        </Box>
        <Box
          sx={{
            minWidth: '100%',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            width: '100%',
            gap: '16px',
          }}
        >
          <Box
            sx={{
              minWidth: '100%',
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              width: '100%',
              padding: '16px',
              backgroundColor: 'gray-background.main',
              borderRadius: '4.5px',
            }}
          >
            <Icon sx={{ color: 'warning.main' }}>help_outline</Icon>
            <Typography sx={{ width: '100%' }}>Candidate no-show</Typography>
          </Box>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'flex-end',
              width: '100%',
              gap: '6px',
            }}
          >
            <TextField
              value={remarks}
              fullWidth
              sx={{ height: '137px' }}
              multiline={true}
              rows={5}
              inputProps={{ maxLength: 120 }}
              placeholder="Add any remark (Optional)"
              onChange={(e) => {
                setTextLength(e.target.value.length)
                setRemarks(e.target.value)
              }}
            />
            <Typography sx={{ textAlign: 'end' }}>{textLength}/120</Typography>
          </Box>
        </Box>
        <Box sx={{ width: '100%' }} display="flex" flexDirection="column" columnGap={2}>
          <AdxButton
            variant={'contained'}
            sx={{
              boxShadow: 'none',
              backgroundColor: 'alternate.main',
              '&:hover': {
                backgroundColor: 'alternate.main',
                opacity: 0.9,
              },
            }}
            fullWidth
            isLoading={isLoading}
            onClick={() => onClick(remarks)}
          >
            Confirm
          </AdxButton>
          <AdxButton variant={'ghost'} sx={{ color: 'alternate.main' }} fullWidth onClick={onClose}>
            Cancel
          </AdxButton>
          {isError && (
            <Typography variant="body1" color="error.main">
              An error has occured. Please try again
            </Typography>
          )}
        </Box>
      </Box>
    </Modal>
  )
}
const CancelInterviewModal = ({ open, onClose, isLoading, onClick, isError }) => {
  const [textLength, setTextLength] = useState(0)
  const [remarks, setRemarks] = useState('')

  const style = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    maxWidth: '600px',
    width: '100%',
    bgcolor: 'background.paper',
    boxShadow: 24,
    borderRadius: '6.5px',
    padding: '40px 32px 32px 32px',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '40px',
    position: 'relative',
  }
  return (
    <Modal open={open} onClose={onClose}>
      <Box sx={style}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '6px',
          }}
        >
          <Typography variant="h4" sx={{ width: '341px', textAlign: 'center' }}>
            Do you confirm the cancellation of the interview session?
          </Typography>
          <Typography variant="body1">
            You may add a remark of the interview cancellation.
          </Typography>
        </Box>
        <Box
          sx={{
            minWidth: '100%',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            width: '100%',
            gap: '16px',
          }}
        >
          <Box
            sx={{
              minWidth: '100%',
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              width: '100%',
              padding: '16px',
              backgroundColor: 'gray-background.main',
              borderRadius: '4.5px',
            }}
          >
            <Icon sx={{ color: 'error.main' }}>highlight_off</Icon>
            <Typography sx={{ width: '100%' }}>Interview cancelled</Typography>
          </Box>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'flex-end',
              width: '100%',
              gap: '6px',
            }}
          >
            <TextField
              value={remarks}
              fullWidth
              sx={{ height: '137px' }}
              multiline={true}
              rows={5}
              inputProps={{ maxLength: 120 }}
              placeholder="Add any remark (Optional)"
              onChange={(e) => {
                setTextLength(e.target.value.length)
                setRemarks(e.target.value)
              }}
            />
            <Typography sx={{ textAlign: 'end' }}>{textLength}/120</Typography>
          </Box>
        </Box>
        <Box sx={{ width: '100%' }} display="flex" flexDirection="column" columnGap={2}>
          <AdxButton
            variant={'contained'}
            sx={{
              boxShadow: 'none',
              backgroundColor: 'alternate.main',
              '&:hover': {
                backgroundColor: 'alternate.main',
                opacity: 0.9,
              },
            }}
            fullWidth
            isLoading={isLoading}
            onClick={() => onClick(remarks)}
          >
            Yes, cancel interview
          </AdxButton>
          <AdxButton variant={'ghost'} sx={{ color: 'alternate.main' }} fullWidth onClick={onClose}>
            No, keep interview
          </AdxButton>
          {isError && (
            <Typography variant="body1" color="error.main">
              An error has occured. Please try again
            </Typography>
          )}
        </Box>
      </Box>
    </Modal>
  )
}

const CompleteInterviewModal = ({ open, onClose, isLoading, onClick, isError }) => (
  <AdxInfoModal
    modalWidth={600}
    muiIcon="error"
    open={open}
    onClose={onClose}
    iconColor="warning.main"
    title="Do you confirm to mark this session as completed?"
    subtitle=""
    footerButtons
    firstBtnLabel={'Yes'}
    firstBtnHandle={onClick}
    secondBtnLabel={'Cancel'}
    secondBtnHandle={onClose}
  >
    {isError && (
      <Typography variant="body1" color="error.main">
        An error has occured. Please try again
      </Typography>
    )}
  </AdxInfoModal>
)
